import React, { useState } from 'react';
import { Field, useForm } from 'react-final-form';
import PhoneInputRaw from 'react-phone-input-2';

const PhoneInput = PhoneInputRaw?.default || PhoneInputRaw;
import classNames from 'classnames';

import { ValidationError } from '../../components';
import { validateLengthByCountry, isInvalidPhoneNumber } from '../../util/validators';
import CountrySelectDropdown from './CountrySelectDropdown';
import css from './FieldPhoneNumberInput.module.css';

const DEFAULT_COUNTRY_OPTIONS_ORDER = ['US', 'GB', 'JP', '...'];

/**
 * Phone number field with optional split country-code storage.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id
 * @param {string} [props.label]
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {string} [props.inputClassName]
 * @param {string} [props.labelClassName]
 * @param {string} [props.countryCodeName]
 * @param {string} [props.phoneNumberName]
 * @param {Function} [props.validate]
 * @param {boolean} [props.disabled]
 * @param {string} [props.defaultCountry]
 * @param {Array} [props.countryOptionsOrder]
 * @param {boolean} [props.addInternationalOption]
 * @param {boolean} [props.useScrollableCountrySelect] Use custom country dropdown with max-height
 * @param {number} [props.countrySelectMaxHeight] Max height for scrollable country dropdown (px)
 * @param {React.ElementType} [props.countrySelectComponent]
 * @param {Object} [props.countrySelectProps]
 * @returns {JSX.Element}
 */
const FieldPhoneNumberInputComponent = props => {
  const {
    id,
    label,
    placeholder,
    className,
    rootClassName,
    inputClassName,
    labelClassName,
    countryCodeName,
    phoneNumberName = 'phoneNumber',
    validate,
    invalidMessage = 'Please enter a valid phone number.',
    disabled,
    defaultCountry = 'us',
    useScrollableCountrySelect = false,
    countrySelectMaxHeight = 240,
    countrySelectComponent,
    countrySelectProps,
  } = props;

  const form = useForm();
  const [selectedCountry, setSelectedCountry] = useState(() => {
    return typeof defaultCountry === 'string' ? defaultCountry.toLowerCase() : 'us';
  });

  // Preserve max-height country-select API from previous implementation.
  // Applied to react-phone-input-2 dropdown via CSS variable.
  const resolvedCountrySelectComponent =
    countrySelectComponent || (useScrollableCountrySelect ? CountrySelectDropdown : undefined);
  const resolvedCountrySelectProps = {
    ...(useScrollableCountrySelect ? { maxHeight: countrySelectMaxHeight } : null),
    ...countrySelectProps,
  };
  const dropdownMaxHeight =
    resolvedCountrySelectProps.maxHeight ?? countrySelectMaxHeight ?? 240;

  const combinedValidate = (value, allValues) => {
    let customErr;
    if (validate) {
      customErr = validate(value, allValues);
    }
    if (customErr) {
      return customErr;
    }

    if (!value || typeof value !== 'string' || value.trim() === '') {
      return undefined;
    }

    const rawDigits = value.trim().replace(/\D/g, '');

    let countryCode = '';
    if (countryCodeName) {
      countryCode = allValues?.[countryCodeName] || '';
    } else if (selectedCountry) {
      countryCode = selectedCountry;
    }

    if (isInvalidPhoneNumber(value, countryCode)) {
      return invalidMessage;
    }

    const isValidLength = validateLengthByCountry(countryCode, rawDigits);
    if (!isValidLength) {
      return invalidMessage;
    }

    return undefined;
  };

  return (
    <Field
      name={phoneNumberName}
      validate={combinedValidate}
      render={({ input, meta }) => {
        const hasCountryCodeField = !!countryCodeName;

        let combinedValue = '';
        if (hasCountryCodeField) {
          const values = form.getState().values;
          const countryCode = values[countryCodeName] || '';
          const phoneNumber = input.value || '';
          const codeDigits = countryCode.replace(/\D/g, '');
          const phoneDigits = phoneNumber.replace(/\D/g, '');
          combinedValue = `${codeDigits}${phoneDigits}`;
        } else {
          combinedValue = (input.value || '').replace(/\D/g, '');
        }

        const handleChange = (val, countryData) => {
          if (countryData && countryData.countryCode) {
            setSelectedCountry(countryData.countryCode.toLowerCase());
          }

          if (!hasCountryCodeField) {
            input.onChange(val ? `+${val}` : '');
            return;
          }

          if (!val) {
            form.change(countryCodeName, '');
            input.onChange('');
            return;
          }

          const dialCode = countryData?.dialCode ? `+${countryData.dialCode}` : '';
          let nationalNumber = val;
          if (countryData?.dialCode && val.startsWith(countryData.dialCode)) {
            nationalNumber = val.slice(countryData.dialCode.length);
          }

          form.change(countryCodeName, dialCode);
          input.onChange(nationalNumber);
        };

        const showFieldError = meta.touched || (meta.dirty && !!meta.error);
        const hasError = showFieldError && meta.error;
        const classes = classNames(rootClassName || css.root, className);

        return (
          <div className={classes}>
            {label ? <label htmlFor={id} className={labelClassName}>{label}</label> : null}
            <div
              className={classNames(css.phoneInput2Wrapper, { [css.inputError]: hasError }, inputClassName)}
              style={{ '--phone-country-dropdown-max-height': `${dropdownMaxHeight}px` }}
            >
              <PhoneInput
                country={selectedCountry}
                preferredCountries={['us', 'gb', 'jp', 'in']}
                value={combinedValue}
                onChange={handleChange}
                enableSearch
                disableSearchIcon
                searchPlaceholder="Search country..."
                searchNotFound="No country found"
                placeholder={placeholder}
                disabled={disabled}
                inputProps={{
                  id,
                  name: phoneNumberName,
                  onBlur: input.onBlur,
                  onFocus: input.onFocus,
                }}
                containerClass={css.phoneInputContainer}
                inputClass={css.phoneInputControl}
                buttonClass={css.phoneInputButton}
                dropdownClass={css.phoneInputDropdown}
                searchClass={css.phoneInputSearch}
              />
            </div>
            <ValidationError fieldMeta={{ ...meta, touched: showFieldError }} />
          </div>
        );
      }}
    />
  );
};

export { CountrySelectDropdown };
export default FieldPhoneNumberInputComponent;
